<script>
import { Library } from "@observablehq/stdlib/";
import { D3Utility } from "../services/d3_utility.js";

export default {
  name: "DiffChart",
  props: {
    data: {
      required: true,
      type: Array,
    },
    chartData: {
      required: true,
      type: Object,
    },
    duration: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      xScale: null,
      line: null,
      lines: null,
      bars: null,
      barWidth: null,
      yLineScale: null,
      gx: null,
      svg: null,
      xaxis: null,
      min_translate_x: null,
      max_translate_x: null,
      width: 700,
      height: 300,
      margin: 30,
      padding: 30,
      zoom_event: null,
      updated_data: null,
      clip: null,
      color: null,
      div: null,
      merged_data: null,
      area: null,
      clip_paths: null,
      legends: null,
    };
  },
  watch: {
    data: function (newVal) {
      if ((!newVal || newVal.length == 0) && this.svg) this.svg.remove();
      if (newVal && newVal.length > 0 && !this.svg) {
        this.init();
      } else if (this.svg && this.zoom_event) {
        this.updateChart();
        this.handleZoom(this.zoom_event);
      }
    },
    duration: function () {},
  },
  mounted() {
    if (this.data && this.data.length > 0) this.init();
  },
  methods: {
    init() {
      let self = this;
      let colorScheme = this.chartData.colors;
      const id = this.chartData.id;
      const width = D3Utility.get_conatiner_width(".container");
      this.width = width;
      const height = 300;
      const legendHeight = 100;
      const margin = 30;
      const padding = 30;
      this.chart_config = {
        width,
        height,
        legendHeight,
        margin,
        padding,
      };
      const zoom = D3Utility.define_restricted_zoom(this.chart_config, zoomed);

      var keys =
        this.data.map((elem) => {
          return elem.id;
        }) || [];

      this.color = D3Utility.get_scale_ordinal(keys, colorScheme);
      this.xScale = D3Utility.get_scaleband([margin, width - margin - padding]);
      this.yLineScale = D3Utility.get_linear_scale([
        height - margin - padding,
        margin,
      ]);
      this.updated_data = {
        line: [],
        bar: [],
      };
      this.data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });
      this.xScale.domain(
        this.data[0].values.map(function (d) {
          return d.key;
        })
      );
      this.yLineScale
        .domain([
          D3Utility.get_min(this.updated_data["line"]),
          D3Utility.get_max(this.updated_data["line"]),
        ])
        .nice();
      //-----------------------------AXES-----------------------------//
      var dates = this.data[0].values.map(function (v) {
        return v.key;
      });

      var yLineAxis = (g, y) =>
        g
          .attr("transform", `translate(${margin + padding},0)`)
          .call(D3Utility.define_left_axis(y));

      this.xaxis = (g, x) =>
        g
          .attr(
            "transform",
            `translate(${margin},${height - margin - padding})`
          )
          .call(D3Utility.define_bottom_axis_non_con(x, this.xScale));
      this.div = D3Utility.append_tooltip();
      this.svg = D3Utility.create_svg(this.chartData.id, this.chart_config);
      const library = new Library();
      this.clip = library.DOM.uid("clip");

      this.svg = D3Utility.append_clip_path(
        this.chart_config,
        this.clip,
        this.svg
      );

      //----------------------------LINES-----------------------------//

      this.line = D3Utility.get_line_obj(this.xScale, this.yLineScale);

      //-------------------------2. DRAWING---------------------------//
      //-----------------------------AXES-----------------------------//

      this.gx = D3Utility.append_axis(
        this.svg,
        this.xaxis,
        this.xScale,
        padding,
        height - 2 * padding
      );
      this.gx
        .append("text")
        .attr("x", "650px")
        .attr("y", "30px")
        .attr("text-anchor", "middle")
        .text(this.chartData.xAxis);

      let yaxis = D3Utility.append_axis(
        this.svg,
        yLineAxis,
        this.yLineScale,
        2 * padding,
        0
      );
      yaxis
        .append("text")
        .attr("x", "-130px")
        .attr("y", "-30px")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .text(this.chartData.yAxis);

      //----------------------------LINES-----------------------------//
      this.addLinesAndBars();
      this.svg
        .call(zoom)
        .transition()
        .duration(750)
        .call(zoom.scaleTo, this.duration == 1 ? 6 : 1);

      function zoomed(event) {
        self.zoom_event = event;
        self.handleZoom(event);
      }
    },
    handleZoom(event) {
      let self = this;
      this.xScale.range(
        [this.margin, this.width - this.margin - this.padding].map((d) =>
          event.transform.applyX(d)
        )
      );
      this.lines.selectAll("path").attr("d", function (d) {
        return self.line(d.values);
      });
      this.lines
        .selectAll("circle")
        .attr("r", () =>
          event.transform.k * 1.2 > 2 ? 2 : event.transform.k * 0.5
        )
        .attr("cx", (d) => self.xScale(d.key));

      this.area.y0(0);
      this.svg
        .selectAll(".above_clip path")
        .attr("d", this.area(self.merged_data));
      this.area.y0(self.height - 2 * (self.margin + self.padding));
      this.svg
        .selectAll(".below_clip path")
        .attr("d", this.area(self.merged_data));
      this.area.y0(function (d) {
        return self.yLineScale(d.value1);
      });
      this.svg.selectAll(".area.above").attr("d", this.area(self.merged_data));

      this.svg.selectAll(".area.below").attr("d", this.area(self.merged_data));
      this.xaxis = (g, x) =>
        g
          .attr(
            "transform",
            `translate(${this.margin},${
              this.height - this.margin - this.padding
            })`
          )
          .call(D3Utility.define_bottom_axis_non_con(x, self.xScale, event));

      this.gx.call(this.xaxis, self.xScale);
    },
    updateChart() {
      this.updated_data = {
        line: [],
        bar: [],
      };
      this.data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });
      let self = this;
      this.lines.remove();
      this.clip_paths.remove();
      this.addLinesAndBars();
      this.gx.call(this.xaxis, this.xScale);
    },
    addLinesAndBars() {
      let self = this;
      this.merged_data = this.updated_data["line"][0].values.map((item, i) => {
        let el = Object.assign({}, item);
        el.value1 = item.value;
        return Object.assign({}, el, this.updated_data["line"][1].values[i]);
      });
      this.area = D3Utility.get_area_obj(this.xScale, this.yLineScale);
      const library = new Library();
      let above_id = library.DOM.uid("clip-above");
      let below_id = library.DOM.uid("clip-below");
      this.clip_paths = this.svg
        .append("g")
        .attr("transform", "translate(" + this.padding + ",0)")
        .attr("clip-path", this.clip)
        .attr("class", "clip_paths");
      this.area.y0(0);
      this.clip_paths
        .append("clipPath")
        .attr("id", above_id)
        .attr("class", "above_clip")
        .append("path")
        .attr("clip-path", self.clip)
        .attr("d", this.area(self.merged_data));
      this.area.y0(self.height - 2 * (self.margin + self.padding));
      this.clip_paths
        .append("clipPath")
        .attr("id", below_id)
        .attr("class", "below_clip")
        .append("path")
        .attr("clip-path", self.clip)
        .attr("d", this.area(self.merged_data));
      this.area.y0(function (d) {
        return self.yLineScale(d.value);
      });
      this.clip_paths
        .append("path")
        .attr("class", "area above")
        .attr("clip-path", `url(#${above_id})`)
        .attr("d", this.area(self.merged_data))
        .attr("opacity", 0.4)
        .attr("fill", this.color(this.updated_data["line"][1].id));

      this.clip_paths
        .append("path")
        .attr("class", "area below")
        .attr("clip-path", `url(#${below_id})`)
        .attr("d", this.area(self.merged_data))
        .attr("opacity", 0.4)
        .attr("fill", this.color(this.updated_data["line"][0].id));
      this.lines = D3Utility.append_lines(
        this.svg,
        this.updated_data["line"],
        this.chart_config,
        this.clip,
        this.line,
        this.color,
        this.xScale,
        this.yLineScale,
        this.div,
        this.chartData
      );

      var keys = this.data.map((elem) => {
        return { label: elem.label, id: elem.id };
      });

      // Add one dot in the legend for each name.
      this.legends = D3Utility.append_group(this.svg, 0, this.height);
      this.legends = D3Utility.append_legends(
        this.svg,
        this.chartData.id,
        0,
        this.height,
        keys,
        this.chart_config,
        this.color
      );
    },
  },
  computed: {},
};
</script>

<template>
  <div class="container">
    <div :id="`${chartData.id}`" class="svg-container"></div>
  </div>
</template>

<style lang="scss">
/* AXES */
/* ticks */
.axis line {
  stroke: #706f6f;
  stroke-width: 0.5;
  shape-rendering: crispEdges;
}

/* axis contour */
.axis path {
  stroke: #706f6f;
  stroke-width: 0.7;
  shape-rendering: crispEdges;
}

/* axis text */
.axis text {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 120%;
}

/* LINE CHART */
path.line-0 {
  fill: none;
  // stroke: #ed3700;
  stroke: url(#ptr);
}

path.line-1 {
  fill: none;
  stroke: #2b2929;
  stroke-dasharray: 2;
}

path.line-2 {
  fill: none;
  stroke: #9c9c9c;
  stroke-dasharray: 6;
}

.serie_label {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 80%;
}
.zoom {
  cursor: move;
  fill: none;
  pointer-events: all;
}
.bars rect {
  opacity: 0.4;
  stroke: black;
}
.pointer {
  cursor: pointer;
}
div.tooltip {
  position: absolute;
  width: 200px;
  height: 110px;
  background-color: white;
  padding: 10px;
  font-size: 12px;
  pointer-events: none;
  border: none;
  box-shadow: -3px -1px 20px 0 rgba(0, 0, 0, 0.18),
    0 6px 20px 0 rgba(153, 155, 168, 0.1);
}
div.tooltip > div {
  margin: 2px 0;
}
.caption {
  color: var(--v-primaryGrey-base);
}
.shadow,
path:hover {
  -webkit-filter: drop-shadow(4px -4px 4px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(4px -4px 4px rgba(0, 0, 0, 0.7));
}
rect:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}
</style>